// Approval Template Integration Component
// Template-specific version of ApprovalIntegration for creating/editing approval templates
// This is a copy of ApprovalIntegration.js adapted for templates

import React, { useState, useEffect, useRef } from 'react';
import { 
  Button, 
  Input, 
  Textarea, 
  Select, 
  SelectItem, 
  Label,
  Switch
} from '../../../../components/ui';
import { useFlexibleList } from '../../../../hooks/useSWR';
import { usePriorities } from '../../hooks/usePriorities';

const ApprovalTemplateIntegration = ({ 
  entityType, 
  entityId, 
  entityData,
  onSubmit,
  onCancel,
  isLoading = false,
  existingTemplate = null,
  isEditMode = false
}) => {
  // Fetch users for approver selection
  const { data: users, isLoading: usersLoading, isError: usersError } = useFlexibleList('/users');
  // Approver pickers should only offer active users
  const activeUsers = (users || []).filter((user) => user.userIsActive === true);
  
  // Fetch priorities from API
  const { priorities, isLoading: prioritiesLoading } = usePriorities();
  
  // Fetch entity types from API
  const { data: entityTypes, isLoading: entityTypesLoading, isError: entityTypesError } = useFlexibleList('/entity-type');
  
  // Track if form has been initialized to prevent overwriting user changes
  const hasInitialized = useRef(false);

  const [formData, setFormData] = useState({
    title: entityData?.documentTitle || entityData?.title || 'Untitled Template',
    description: entityData?.description || '',
    entityTypeId: '', // Entity type ID
    priority: 'medium', // Default to Medium (as string to match Select value)
    approvalType: '', // No default - user must select
    workflowType: '', // No default - user must select
    defaultDueDays: '', // Number of days for due date calculation
    isActive: true, // Default to active
    approvers: [], // Array of approver IDs
    approverType: 'user' // 'user', 'role', or 'department'
  });

  // Populate form with existing template data when in edit mode (only once)
  useEffect(() => {
    // Only initialize once when in edit mode and we have the template data
    if (isEditMode && existingTemplate && !hasInitialized.current) {
      // Wait for priorities to load before setting priority value
      if (priorities && priorities.length > 0) {
        // Convert priorityId to priority name for the select
        let priorityValue = 'medium'; // default
        if (existingTemplate.priorityId) {
          const priorityObj = priorities.find(p => p.id === existingTemplate.priorityId);
          priorityValue = priorityObj ? priorityObj.name.toLowerCase() : 'medium';
        } else if (existingTemplate.priority?.name) {
          priorityValue = existingTemplate.priority.name.toLowerCase();
        }
        
        setFormData({
          title: existingTemplate.title || existingTemplate.name || '',
          description: existingTemplate.description || '',
          entityTypeId: existingTemplate.entityTypeId?.toString() || '',
          priority: priorityValue,
          approvalType: existingTemplate.approvalType || '',
          workflowType: existingTemplate.workflowType || '',
          defaultDueDays: existingTemplate.defaultDueDays?.toString() || '',
          isActive: existingTemplate.isActive !== undefined ? existingTemplate.isActive : true,
          approvers: existingTemplate.approvers || existingTemplate.steps?.map(step => step.approverId?.toString()).filter(Boolean) || [],
          approverType: existingTemplate.approverType || existingTemplate.steps?.[0]?.approverType || 'user'
        });
        
        hasInitialized.current = true;
      }
    } else if (!isEditMode) {
      // Reset initialization flag when not in edit mode
      hasInitialized.current = false;
    }
  }, [isEditMode, existingTemplate, priorities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate approvers are selected
    if (formData.approvers.length === 0) {
      alert('Please select at least one approver');
      return;
    }

    // Validate workflow type is selected
    if (!formData.workflowType || formData.workflowType === '') {
      alert('Please select a workflow type');
      return;
    }

    // Validate approval type is selected (required)
    if (!formData.approvalType || formData.approvalType === '') {
      alert('Please select an approval type');
      return;
    }

    // Validate priority is selected
    const priorityId = getPriorityId(formData.priority);
    if (!priorityId) {
      alert('Please select a valid priority');
      return;
    }
    

    // Map frontend form data to backend DTO format for templates
    const backendData = {
      title: formData.title,
      description: formData.description,
      priorityId: priorityId, // Use validated priorityId
      entityTypeId: formData.entityTypeId ? parseInt(formData.entityTypeId, 10) : (getEntityTypeId(entityType) || null),
      approvalType: formData.approvalType,
      workflowType: formData.workflowType,
      defaultDueDays: formData.defaultDueDays ? parseInt(formData.defaultDueDays, 10) : null,
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      approvers: formData.approvers, // Include approvers array
      approverType: formData.approverType // Include approver type
      // Note: dueDate is NOT included for templates - it will be calculated at request time
    };
    
    try {
      // For templates, always use onSubmit - don't call create/update directly
      // The ApprovalTemplateForm will handle the API calls
      if (onSubmit) {
        await onSubmit(backendData);
      }
    } catch (error) {
      console.error('Error saving approval template:', error);
      alert(`Error saving approval template: ${error.message}`);
    }
  };

  // Helper function to get priority ID from priority name or ID
  const getPriorityId = (priority) => {
    // If priority is already a number (ID), return it
    if (typeof priority === 'number') {
      return priority;
    }
    // If priority is a string (name), find the ID
    const priorityObj = priorities.find(p => p.name.toLowerCase() === priority.toLowerCase());
    console.log('getPriorityId:', { priority, priorityObj, priorities });
    return priorityObj ? priorityObj.id : null;
  };

  // Helper function to map entity types to IDs
  const getEntityTypeId = (entityType) => {
    const entityTypeMap = {
      'document': 1,           // Document
      'document_revision': 1,  // Document revisions also use Document entity type
      'revision': 2,           // Revision
      'capa': 3,               // CAPA
      'risk_assessment': 4,    // Risk Assessment
      'non_conformity': 5,     // Non-Conformity Report
      'change_request': 6,     // Change Request
      'complaint': 7,           // Complaint
      'supplier': 8,           // Supplier
      'supplier_rating': 9,    // Supplier Rating
      'training': 10,          // Training
      'todo': 11,              // Todo
      'capa_actions': 12,       // CAPA Actions
      'risk_mitigation': 13,   // Risk Mitigation Actions
      'complaint_actions': 14, // Complaint Actions
      'file_attachments': 15   // FileAttachments
    };
    return entityTypeMap[entityType] || 1; // Default to document
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value,
        // Reset approvers when approval type changes
        ...(name === 'approvalType' && { 
          approvers: value === 'single' ? [] : prev.approvers || [] 
        })
      };
      
      // Update title and description based on workflow type
      if (name === 'workflowType') {
        const entityName = entityData?.documentTitle || entityData?.title || `${entityType} #${entityId}`;
        switch (value) {
          case 'review':
            newFormData.title = `Review Template for ${entityName}`;
            newFormData.description = `Review template for ${entityType} #${entityId}`;
            break;
          case 'approval':
            newFormData.title = `Approval Template for ${entityName}`;
            newFormData.description = `Approval template for ${entityType} #${entityId}`;
            break;
          case 'review_approval':
            newFormData.title = `Review and Approval Template for ${entityName}`;
            newFormData.description = `Review and approval template for ${entityType} #${entityId}`;
            break;
          default:
            newFormData.title = `Review and Approval Template for ${entityName}`;
            newFormData.description = `Review and approval template for ${entityType} #${entityId}`;
            break;
        }
      }
      
      return newFormData;
    });
  };

  const handleApproverChange = (approverId, isSelected) => {
    setFormData(prev => {
      if (isSelected) {
        return {
          ...prev,
          approvers: [...prev.approvers, approverId]
        };
      } else {
        return {
          ...prev,
          approvers: prev.approvers.filter(id => id !== approverId)
        };
      }
    });
  };

  const getApproverFields = () => {
    const { approverType } = formData;
    
    // Show the appropriate selection based on type (approverType is now shown separately in the form)
    const getSelectionFields = () => {
      if (approverType === 'user') {
        return getUserSelectionFields();
      } else if (approverType === 'role' || approverType === 'department') {
        return null; // Role/department selection will be handled at request time
      }
      return null;
    };

    const getUserSelectionFields = () => {
      const { approvalType } = formData;
      
      switch (approvalType) {
        case 'single':
          return (
            <div>
              <Label htmlFor="singleApprover" required>
                Select Approver
              </Label>
              <Select
                id="singleApprover"
                name="singleApprover"
                value={formData.approvers && formData.approvers.length > 0 ? formData.approvers[0] : ''}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    approvers: selectedValue ? [selectedValue] : []
                  }));
                }}
                disabled={usersLoading || activeUsers.length === 0}
              >
                <SelectItem value="">Select an approver</SelectItem>
                {activeUsers.length > 0 ? (
                  activeUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || user.userEmail || `User ${user.id}`}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="" disabled>No users available</SelectItem>
                )}
              </Select>
              {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
              {usersError && !usersLoading && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
              {!usersLoading && !usersError && activeUsers.length === 0 && <div className="text-xs text-yellow-500 mt-1">No users available</div>}
            </div>
          );
        
        case 'sequential':
          return (
            <div>
              <Label htmlFor="sequentialApprovers" required>
                Select Approvers (in order)
              </Label>
              <div className="space-y-2">
                {formData.approvers.map((approverId, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm font-medium w-8">{index + 1}.</span>
                    <Select
                      value={approverId}
                      onChange={(e) => {
                        const newApprovers = [...formData.approvers];
                        newApprovers[index] = e.target.value;
                        setFormData(prev => ({ ...prev, approvers: newApprovers }));
                      }}
                      disabled={usersLoading}
                      className="flex-1"
                    >
                      <SelectItem value="">Select approver</SelectItem>
                      {activeUsers.filter(user => !formData.approvers.includes(user.id.toString()) || user.id.toString() === approverId)
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                          </SelectItem>
                        ))}
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newApprovers = formData.approvers.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, approvers: newApprovers }));
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, approvers: [...prev.approvers, ''] }));
                  }}
                  disabled={usersLoading}
                >
                  Add Approver
                </Button>
              </div>
              {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
              {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
            </div>
          );
        
        case 'parallel':
          return (
            <div>
              <Label htmlFor="parallelApprovers" required>
                Select Approvers (all must approve)
              </Label>
              <div className="space-y-2">
                {activeUsers.map((user) => (
                  <div key={user.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`approver-${user.id}`}
                      checked={formData.approvers.includes(user.id.toString())}
                      onChange={(e) => handleApproverChange(user.id.toString(), e.target.checked)}
                      disabled={usersLoading}
                      className="rounded"
                    />
                    <label htmlFor={`approver-${user.id}`} className="text-sm">
                      {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                    </label>
                  </div>
                ))}
              </div>
              {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
              {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
            </div>
          );
        
        case 'any_one':
          return (
            <div>
              <Label htmlFor="anyOneApprovers" required>
                Select Approvers (any one can approve)
              </Label>
              <div className="space-y-2">
                {activeUsers.map((user) => (
                  <div key={user.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`approver-${user.id}`}
                      checked={formData.approvers.includes(user.id.toString())}
                      onChange={(e) => handleApproverChange(user.id.toString(), e.target.checked)}
                      disabled={usersLoading}
                      className="rounded"
                    />
                    <label htmlFor={`approver-${user.id}`} className="text-sm">
                      {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                    </label>
                  </div>
                ))}
              </div>
              {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
              {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
            </div>
          );
        
        default:
          return null;
      }
    };

    return (
      <div className="space-y-4">
        {getSelectionFields()}
        {approverType === 'role' && (
          <div>
            <Label>Role-based Approval</Label>
            <div className="text-xs text-gray-500 mt-1">
              Roles will be assigned at request time based on the template configuration.
            </div>
          </div>
        )}
        {approverType === 'department' && (
          <div>
            <Label>Department-based Approval</Label>
            <div className="text-xs text-gray-500 mt-1">
              Departments will be assigned at request time based on the template configuration.
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        {/* Title */}
        <div>
          <Label htmlFor="title" required>
            Template Name
          </Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            placeholder="Enter template name"
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description" required>
            Description
          </Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={3}
            placeholder="Describe what this template is for"
          />
        </div>

        {/* Entity Type, Priority, and Active */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="entityTypeId">
              Entity Type
            </Label>
            <Select
              id="entityTypeId"
              name="entityTypeId"
              value={formData.entityTypeId || ''}
              onChange={handleInputChange}
              disabled={entityTypesLoading}
            >
              <SelectItem value="">Select Entity Type</SelectItem>
              {entityTypesLoading ? (
                <SelectItem value="" disabled>Loading entity types...</SelectItem>
              ) : entityTypesError ? (
                <SelectItem value="" disabled>Error loading entity types</SelectItem>
              ) : (
                entityTypes?.map((entityType) => (
                  <SelectItem key={entityType.id} value={entityType.id.toString()}>
                    {entityType.name || entityType.typeName || `Entity Type ${entityType.id}`}
                  </SelectItem>
                ))
              )}
            </Select>
          </div>

          <div>
            <Label htmlFor="priority">
              Priority
            </Label>
            <Select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              disabled={prioritiesLoading}
            >
              {prioritiesLoading ? (
                <SelectItem value="">Loading priorities...</SelectItem>
              ) : (
                priorities.map(priority => (
                  <SelectItem key={priority.id} value={priority.name.toLowerCase()}>
                    {priority.name}
                  </SelectItem>
                ))
              )}
            </Select>
          </div>

          <div className="flex items-center">
            <div className="flex items-center space-x-2 pt-6">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onChange={(checked) => {
                  setFormData(prev => ({ ...prev, isActive: checked }));
                }}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Active
              </Label>
            </div>
          </div>
        </div>

        {/* Workflow Type Selection */}
        <div>
          <Label htmlFor="workflowType" required>
            Workflow Type
          </Label>
          <Select
            id="workflowType"
            name="workflowType"
            value={formData.workflowType}
            onChange={handleInputChange}
          >
            <SelectItem value="">Select Workflow Type</SelectItem>
            <SelectItem value="review">Review Only</SelectItem>
            <SelectItem value="approval">Approval Only</SelectItem>
            <SelectItem value="review_approval">Review and Approval</SelectItem>
          </Select>
          <div className="text-xs text-gray-500 mt-1">
            {formData.workflowType === 'review' && 'Template will be used for review workflows'}
            {formData.workflowType === 'approval' && 'Template will be used for approval workflows'}
            {formData.workflowType === 'review_approval' && 'Template will be used for review and approval workflows'}
          </div>
        </div>

        {/* Approval Type, Approver Type, and Default Due Days */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="approvalType" required>
              Approval Type
            </Label>
            <Select
              id="approvalType"
              name="approvalType"
              value={formData.approvalType || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  approvalType: value,
                  approvers: value === 'single' ? [] : prev.approvers || []
                }));
              }}
              required
            >
              <SelectItem value="">Select Approval Type</SelectItem>
              <SelectItem value="single">Single Approver</SelectItem>
              <SelectItem value="sequential">Sequential Approval</SelectItem>
              <SelectItem value="parallel">Parallel Approval</SelectItem>
              <SelectItem value="any_one">Any One Approver</SelectItem>
            </Select>
          </div>

          <div>
            <Label htmlFor="approverType" required>
              Approver Type
            </Label>
            <Select
              id="approverType"
              name="approverType"
              value={formData.approverType}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  approverType: e.target.value,
                  approvers: [] // Reset approvers when type changes
                }));
              }}
            >
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="role">Role</SelectItem>
              <SelectItem value="department">Department</SelectItem>
            </Select>
          </div>

          <div>
            <Label htmlFor="defaultDueDays">
              Default Due Days
            </Label>
            <Input
              id="defaultDueDays"
              name="defaultDueDays"
              type="number"
              min="0"
              value={formData.defaultDueDays}
              onChange={handleInputChange}
              placeholder="Enter number of days (e.g., 7)"
            />
            <div className="text-xs text-gray-500 mt-1">
              Days from today to calculate due date
            </div>
          </div>
        </div>

        {/* Approver Selection */}
        {getApproverFields()}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
         <Button
           type="submit"
           variant="default"
           disabled={isLoading}
         >
           {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Template' : 'Create Template')}
         </Button>
      </div>
    </form>
  );
};

export default ApprovalTemplateIntegration;

